import React from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface FeaturedTestimonialProps {
  testimonial: {
    id: number;
    customerName: string;
    location: string;
    rating: number;
    date: string;
    reviewText: string;
    extendedReview: string;
    puppyVariety: string;
    customerImage: string;
    customerImageAlt: string;
    puppyImages: Array<{
      url: string;
      alt: string;
    }>;
    highlights: string[];
  };
}

const FeaturedTestimonial = ({ testimonial }: FeaturedTestimonialProps) => {
  return (
    <div className="rounded-xl bg-gradient-to-br from-primary/5 to-accent/5 p-8 shadow-warm-lg">
      <div className="mb-6 flex items-center gap-3">
        <Icon name="StarIcon" size={28} variant="solid" className="text-warning" />
        <h3 className="font-heading text-2xl font-semibold text-foreground">
          Featured Review
        </h3>
      </div>

      <div className="grid gap-8 lg:grid-cols-2">
        {/* Left Column - Customer Info & Review */}
        <div>
          <div className="mb-6 flex items-start gap-4">
            <div className="h-20 w-20 flex-shrink-0 overflow-hidden rounded-full">
              <AppImage
                src={testimonial.customerImage}
                alt={testimonial.customerImageAlt}
                className="h-full w-full object-cover"
              />
            </div>
            <div className="flex-1">
              <h4 className="font-heading text-xl font-semibold text-foreground">
                {testimonial.customerName}
              </h4>
              <p className="text-muted-foreground">{testimonial.location}</p>
              <div className="mt-2 flex items-center gap-2">
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, index) => (
                    <Icon
                      key={index}
                      name="StarIcon"
                      size={20}
                      variant="solid"
                      className={
                        index < testimonial.rating
                          ? 'text-warning' :'text-muted-foreground/30'
                      }
                    />
                  ))}
                </div>
                <span className="font-medium text-foreground">
                  {testimonial.rating}.0
                </span>
              </div>
              <p className="caption mt-1 text-muted-foreground">
                {testimonial.date}
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <p className="text-lg font-medium leading-relaxed text-foreground">
              "{testimonial.reviewText}"
            </p>
            <p className="leading-relaxed text-muted-foreground">
              {testimonial.extendedReview}
            </p>
          </div>

          {/* Highlights */}
          <div className="mt-6 flex flex-wrap gap-2">
            {testimonial.highlights.map((highlight, index) => (
              <span
                key={index}
                className="rounded-full bg-primary/15 px-4 py-2 text-sm font-medium text-primary"
              >
                {highlight}
              </span>
            ))}
          </div>

          <div className="mt-6 flex items-center gap-2 rounded-lg bg-card p-4">
            <Icon name="HeartIcon" size={20} variant="solid" className="text-accent" />
            <span className="font-medium text-foreground">
              {testimonial.puppyVariety} Labrador Puppy
            </span>
          </div>
        </div>

        {/* Right Column - Puppy Images */}
        <div className="grid gap-4 sm:grid-cols-2">
          {testimonial.puppyImages.map((image, index) => (
            <div
              key={index}
              className="overflow-hidden rounded-lg shadow-warm"
            >
              <AppImage
                src={image.url}
                alt={image.alt}
                className="h-64 w-full object-cover transition-smooth hover:scale-105"
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FeaturedTestimonial;